import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { ROUTES } from "routes";

import {
  useTheme,
  Typography,
  Avatar,
  Link,
  AvatarGroup,
  Stack,
  Tooltip,
  Popover,
  Box,
} from "@mui/material";

import { capitalize } from "utils";
import { getNameOrEmailSlug, getPersonLabel, isBrandHeldCertificate } from "utils";
import { date, datetime, relativeOrDateTime } from "utils/date";
import { Search } from "components/input/Search/Search";
import { Image, MENU_PAPER_SX, MENU_ITEM_SX, EMPTY_VALUE } from "@repo/ui";
import { FileBoxIcon, TagIcon } from "lucide-react";
import { CompanyAvatar } from "components/display/CompanyAvatar/CompanyAvatar";
import { PaymentMethodPreview } from "components/display/PaymentMethodPreview";
// VRG-361 — the shared "this row is not real customer data" badge. One component
// across every cell and detail page so the marking cannot drift (§36).
import { ExampleKindBadge } from "components/display/ExampleKindBadge";
// VRG-491 — direct paths, not the `components` barrel: this file is reached from that
// barrel, so going through it would be a cycle (same reason CompanyAvatar does it).
import { InvalidScanIndicator } from "components/display/InvalidScanIndicator";
import { TamperBadge } from "components/display/TamperBadge";
// Every reference cell attaches this so its destination can render "← Back to <list>"
// (working-conventions §42). Derived here rather than passed per call site — the opt-in
// prop that preceded it was omitted by 35 of the 37 shared navigations that needed it.
import { useReferrerState } from "hooks/useReferrerState";
// VRG-236 — same glyph vocabulary as the certificate DETAIL page's Children/Parent/
// Related sections (§28: reuse, don't invent a second visual language for the same
// concept), and the same group-resolution query it already uses.
import { CertificateGroupRoleIcon, type CertificateGroupRole } from "components/display/CertificateGroupMemberPreview";
import { useCertificateGroup } from "hooks/api";
// VRG-777 — `products.images` holds stills AND video in one ordered list, so `images[0]`
// is no longer necessarily a still. `getProductThumbnail` returns the first non-video
// entry; shared in @repo/types so this branch is not re-written at each of the ~20 sites
// that read the column (§36).
import { getProductThumbnail } from "@repo/types";

export const LinkCell = (props: {
  route: any;
  link: any;
  align?: "left" | "center";
}) => {
  const { link, route, align = "center" } = props;
  const referrer = useReferrerState();
  return (
    <Link
      component={RouterLink}
      to={typeof link == "object" ? `${route}?${link.query}` : route}
      state={referrer}
      height="100%"
      sx={{
        maxWidth: "100%",
        width: align === "left" ? "100%" : undefined,
        textDecoration: "none",
        // Underline on hover to signal this cell is a click-through link (the
        // pinned ID column, etc.). Neutral until hovered so the table stays calm.
        "&:hover .LinkCell-text": { textDecoration: "underline" },
        display: "flex",
        alignItems: "center",
        justifyContent: align === "left" ? "flex-start" : "center",
      }}
    >
      <Typography className="LinkCell-text" variant="body3" sx={{ fontWeight: 500 }} noWrap>
        {typeof link == "object" ? link.label : String(link)}
      </Typography>
    </Link>
  );
};

/**
 * A numeric aggregate-reference cell: a count that links to the referenced
 * collection, prefiltered to this row (e.g. a product's certificate count →
 * /certificates?product=<id>). Zero is non-interactive (nothing to drill into).
 */
export const AggregateCountCell = (props: { count: number; to: string; backLabel?: string }) => {
  const { count, to, backLabel } = props;
  // `backLabel` stays supported as an override; without one the label is derived,
  // so this cell no longer silently loses its back-link when a caller omits it.
  const referrer = useReferrerState(backLabel);
  if (!count) {
    return (
      <Stack height="100%" alignItems="center" justifyContent="center">
        <Typography variant="body3" color="text.secondary">
          {count ?? 0}
        </Typography>
      </Stack>
    );
  }
  return (
    <Link
      component={RouterLink}
      to={to}
      state={referrer}
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
      height="100%"
      sx={{
        textDecoration: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "text.primary",
        "&:hover": { textDecoration: "underline" },
      }}
    >
      <Typography variant="body3" noWrap>
        {count}
      </Typography>
    </Link>
  );
};

export const LinkGroupCell = (props: {
  route: any;
  links: Array<{ label: string; query?: string }>;
}) => {
  const { links, route } = props;
  const referrer = useReferrerState();
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      justifyContent="center"
      sx={{ width: "100%" }}
    >
      {links.map((link, index) => {
        return (
          <>
            <Link
              component={RouterLink}
              to={
                typeof link == "object" ? `${route}?${link.query}` : route(link)
              }
              state={referrer}
              height="100%"
              sx={{ maxWidth: "100%" }}
            >
              <Typography noWrap>{link.label}</Typography>
            </Link>
            {index != links.length - 1 && ","}
          </>
        );
      })}
    </Stack>
  );
};

export const ProductCell = (props: {
  product: any;
  route?: string;
  size?: number;
  width?: number;
}) => {
  const { product, route, size = 48, width = 200 } = props;
  const theme = useTheme();
  const referrer = useReferrerState();

  const content = (
    <Stack
      direction="row"
      spacing={3}
      alignItems="center"
      display="flex"
      justifyContent="left"
      sx={{minWidth: '100%'}}
    >
      <Image
        src={getProductThumbnail(product.images) ?? ""}
        alt={product.title}
        fit="cover"
        sx={(theme) => ({
          width: size,
          height: size,
          border: `1px solid ${theme.palette.grey[200]}`,
          transition: "ease-in-out 200ms",
          flexShrink: 0,
        })}
      />
      <Stack spacing={2} sx={{ minWidth: 0, width}}>
        <Typography variant="body3" sx={{ fontWeight: 500 }} noWrap>
          {product.title}
        </Typography>
        {/* VRG-361 — the badge annotates the METADATA line, beside the SKU, not the
            title. The title is the widest thing in the cell and already truncates;
            putting a ~100px badge on its row would crush it on every seeded product.
            This also matches how CertificateCell already annotates its id line. */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
          <Typography variant="body4" color="text.secondary" noWrap>
            {product.sku}
          </Typography>
          <ExampleKindBadge kind={product.example_kind} size="dense" />
        </Stack>
      </Stack>
    </Stack>
  );

  // No route AND no product id (e.g. a tag's self-describing product snapshot with no real
  // product record to link to) — render the cell WITHOUT the Link wrapper (VERA-790).
  if (!route && !product?.id) {
    return (
      <Box
        height="100%"
        sx={{
          p: 2,
          pl: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "left",
        }}
      >
        {content}
      </Box>
    );
  }

  return (
    <Link
      component={RouterLink}
      to={route ? route : ROUTES.PRODUCT(product.id)}
      state={referrer}
      height="100%"
      sx={{
        p: 2,
        pl: 0,
        textDecoration: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "left",
      }}
    >
      {content}
    </Link>
  );
};

/**
 * Split a minted package name into its SKU prefix and its unique run.
 *
 * A package name is minted as `<SKU>-<yymmdd>-<hhmm>-<hex>` (see the API's package-name
 * generator), e.g. `VERA-STK-VER-25-100-260809-1342-1D47` → sku `VERA-STK-VER-25-100`,
 * unique `260809-1342-1D47`. The SKU can itself contain dashes and digits, so the split is
 * anchored to the END of the string, which is the only unambiguous place to anchor it.
 *
 * Returns `null` when the name does not match the minted shape (hand-named or legacy
 * packages), so callers fall back to showing the whole name rather than mangling it.
 */
export const splitPackageName = (
  name?: string | null,
): { sku: string; unique: string } | null => {
  if (!name) return null;
  const match = /^(.*)-(\d{6}-\d{4}-[0-9A-Za-z]{4})$/.exec(name);
  if (!match || !match[1]) return null;
  return { sku: match[1], unique: match[2] };
};

export const PackageCell = (props: {
  pkg: any;
  size?: number;
  width?: number;
  /**
   * Which half of a minted package name leads the cell.
   *
   * - `name` (default) — the whole name on top, the created date beneath. What the Packages
   *   list wants: the SKU prefix is the package's identity there.
   * - `unique` — the `<yymmdd>-<hhmm>-<hex>` run on top, the SKU beneath. For surfaces that
   *   already show the tag's own product in an adjacent column (the internal Tags list), where
   *   the SKU prefix restates it and the timestamp is the only part that identifies WHICH box.
   *
   * Falls back to `name` behaviour for a package whose name isn't in the minted shape.
   */
  nameEmphasis?: "name" | "unique";
}) => {
  const { pkg, size = 48, width = 340, nameEmphasis = "name" } = props;
  const theme = useTheme();
  const product = pkg?.tag_shop_product;
  // Prefer the selected variant's image, fall back to product-level image
  const imageSrc =
    // VRG-777 — deliberately still `images[0]`, and NOT `getProductThumbnail`. This
    // `product` is a `tag_shop_product` — Vera's own tag merchandise, a SEPARATE table
    // (`tag_shop_products.images`) written by the internal tag-shop admin, never by the
    // product upload route that VRG-777 taught to accept video. So this list cannot hold
    // a clip, and importing a product-media helper here would imply it can.
    product?.selected_variant?.image ?? product?.images?.[0] ?? null;
  // The subline is the package's CREATED date, not its SKU. A package's name is minted as
  // `<SKU>-<yymmdd>-<hhmm>-<hex>` (e.g. VERA-STK-VER-25-100-260809-1342-1D47), so an SKU
  // subline was re-stating the first two thirds of the title directly beneath it. The date
  // is the one thing the name compresses into an unreadable digit run.
  const created = pkg?.created_at ?? null;
  const split = nameEmphasis === "unique" ? splitPackageName(pkg?.name) : null;
  // The primary line, and the subline that replaces the created date when the caller asked
  // for the unique run to lead. The full name stays in the tooltip either way, so nothing
  // the split hides becomes unrecoverable.
  const primary = split ? split.unique : pkg?.name;
  const skuSubline = split ? split.sku : null;
  return (
    <Stack
      direction="row"
      spacing={3}
      alignItems="center"
      sx={{ p: 2, pl: 0, height: "100%", minWidth: "100%" }}
    >
      {imageSrc ? (
        <Image
          src={imageSrc}
          alt={product?.selected_variant?.title ?? product?.title ?? pkg?.name}
          fit="cover"
          sx={(theme) => ({
            width: size,
            height: size,
            borderRadius: "6px",
            border: `1px solid ${theme.palette.grey[200]}`,
            flexShrink: 0,
          })}
        />
      ) : (
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{
            width: size,
            height: size,
            borderRadius: "6px",
            border: `1px solid ${theme.palette.grey[200]}`,
            backgroundColor: theme.palette.grey[50],
            flexShrink: 0,
          }}
        >
          <FileBoxIcon
            strokeWidth={1}
            size={22}
            color={theme.palette.grey[400]}
          />
        </Stack>
      )}
      <Stack spacing={2} sx={{ minWidth: 0, width }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
          {/* The name truncates at the pinned column width, and with the Package ID column
              hidden by default it is the row's ONLY identifier — so the full value has to stay
              recoverable. The column width itself is fixed deliberately (it lines the column
              border up with the search box above), so a tooltip is the recovery, not a widen. */}
          <Tooltip title={pkg?.name ?? ""} placement="top" arrow>
            <Typography
              variant="body3"
              sx={{ fontWeight: 500, minWidth: 0 }}
              noWrap
            >
              {primary}
            </Typography>
          </Tooltip>
          {/* VERA-790: the handle-label indicator was removed — a package is now always registered
              WITH its scannable label via Add packages, so the icon was always-on / redundant. */}
        </Stack>
        {/* The SKU subline, when the unique run leads (see `nameEmphasis`). It takes the
            created date's slot rather than stacking a third line — the cell is one table row
            high, and the date is the less useful of the two on a surface that is identifying
            WHICH box a tag came from. */}
        {skuSubline && (
          <Typography
            variant="body4"
            color="text.secondary"
            noWrap
            sx={{ width: "fit-content" }}
          >
            {skuSubline}
          </Typography>
        )}
        {/* Omitted entirely when the caller passes a bare {id, name} (e.g. the Tags list's
            package column) rather than a full package row — an em-dash placeholder would be
            noise, not information. */}
        {!skuSubline && created && (
          <Tooltip title={datetime(created) ?? ""} placement="top" arrow>
            <Typography
              variant="body4"
              color="text.secondary"
              noWrap
              sx={{ width: "fit-content" }}
            >
              {relativeOrDateTime(created)}
            </Typography>
          </Tooltip>
        )}
      </Stack>
    </Stack>
  );
};

export const ProductGroupCell = (props: {
  products: any[];
  route: any;
  // Show the per-product quantity count badge. Opt-in — only meaningful in ORDER
  // contexts (how many were ordered). Off elsewhere (e.g. the Tags Inventory
  // "For products" column, where a product's number isn't an order quantity).
  showQuantityBadge?: boolean;
}) => {
  const { products = [], route, showQuantityBadge = false } = props;
  const referrer = useReferrerState();
  const theme = useTheme();

  // A single product renders the richer single ProductCell (image + title +
  // SKU inline) instead of an overlapping avatar group, which only reads
  // well for 2+. ProductCell's `route` is a STRING (unlike this cell's
  // route function), so call it here.
  if (products.length === 1) {
    return <ProductCell product={products[0]} route={route(products[0]?.id)} />;
  }

  return (
    <AvatarGroup
      max={7}
      sx={{ p: 2, pl: 0, justifyContent: "left", width: "100%" }}
    >
      {products.map((product, index) => {
        // Tooltip content: title on top, SKU muted below. The theme's dark tooltip
        // override isn't reaching this DataGrid-portaled tooltip, so pin the dark
        // surface + a larger, more readable text size explicitly.
        return (
          <Tooltip
            key={product?.id}
            slotProps={{
              tooltip: {
                // Mirrors the global MuiTooltip theme (white in light mode, dark in dark);
                // pinned explicitly because the theme override doesn't reach this
                // DataGrid-portaled tooltip.
                sx: {
                  backgroundColor: theme.palette.mode === 'dark' ? '#141417' : '#ffffff',
                  color: theme.palette.mode === 'dark' ? '#fafafa' : theme.palette.text.primary,
                  fontSize: 14,
                  lineHeight: 1.5,
                  padding: '10px 12px',
                  borderRadius: 6,
                  maxWidth: 280,
                  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : theme.palette.divider}`,
                  boxShadow: theme.shadows[3],
                },
              },
            }}
            title={
              product?.title || product?.sku ? (
                <>
                  {product?.title && <div>{product.title}</div>}
                  {product?.sku && (
                    <div style={{ opacity: 0.7 }}>{product.sku}</div>
                  )}
                </>
              ) : (
                ""
              )
            }
          >
            <Link
              component={RouterLink}
              to={route(product?.id)}
              state={referrer}
              underline="none"
              height="100%"
              sx={{ marginLeft: index != 0 ? -2 : 0, display: "flex", position: "relative" }}
            >
              {/* §36 parity fix: no forced background here (was `#27272a` unconditionally,
                  even once a real image loaded — for an asset with transparency, e.g. a
                  logo PNG, that read as a colored halo the single-product ProductCell
                  never showed). The missing-image placeholder background now lives in
                  the shared Image component itself (only for its failed/no-src state),
                  so this and ProductCell render identically. Border matches ProductCell's
                  1px treatment (was a heavier 2px override). */}
              <Image
                src={getProductThumbnail(product.images) ?? ""}
                alt={product.title}
                fit="cover"
                sx={(theme) => ({
                  width: 48,
                  height: 48,
                  border: `1px solid ${theme.palette.grey[200]}`,
                  transition: "ease-in-out 200ms",
                  flexShrink: 0,
                })}
              />
              {/* Quantity badge — one preview + a count instead of N duplicate
                  previews when more than one of a product is ordered. Sits top-RIGHT
                  so it stays visible when avatars overlap (a top-left badge gets
                  covered by the neighbour stacking over it). Neutral zinc, not brand
                  purple — purple is reserved for real attention, this is just a count. */}
              {showQuantityBadge && Number(product?.quantity) > 1 && (
                <Box
                  sx={(theme) => ({
                    position: "absolute",
                    top: -6,
                    right: -6,
                    minWidth: 20,
                    height: 20,
                    px: 0.5,
                    borderRadius: "10px",
                    bgcolor: theme.palette.mode === "dark" ? "#3f3f46" : "#52525b",
                    color: "#fff",
                    fontSize: 11,
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: `2px solid ${theme.palette.background.paper}`,
                    zIndex: 2,
                  })}
                >
                  {product.quantity}
                </Box>
              )}
            </Link>
          </Tooltip>
        );
      })}
    </AvatarGroup>
  );
};

// VRG-236 — group-member count for a group PARENT row. Fetched lazily via the
// same query CertificatePage's Children/Parent/Related sections already use
// (React Query caches by certificateId, so an expanded detail panel for the
// same row reuses this call rather than duplicating it). Renders nothing while
// loading/absent — the role glyph next to it is the synchronous, always-present
// "this is a group" signal; the count is a light enhancement, not something
// worth a skeleton (§19 exempts instantaneous/supplementary badges).
const GroupMemberCountBadge = ({ certificateId }: { certificateId: string }) => {
  const { data } = useCertificateGroup({ certificateId });
  const count = data?.children?.length;
  if (!count) return null;
  return (
    <Typography variant="body4" color="text.secondary" noWrap sx={{ flexShrink: 0 }}>
      {count} {count === 1 ? "item" : "items"}
    </Typography>
  );
};

export const CertificateCell = (props: { certificate: any; size?: number; width?: number; showGroupBadge?: boolean }) => {
  const { certificate, size = 48, width = 150, showGroupBadge = false } = props;
  const theme = useTheme();
  const referrer = useReferrerState();
  // VRG-236 — role within a certificate group, resolved directly off the row
  // (no fetch): a CHILD always carries its own `parent_certificate_id`; a
  // PARENT carries `group_divisibility` while having no parent of its own
  // (both denormalized onto every member — see CertificateGroupMemberPreview,
  // the same glyph vocabulary reused here). `showGroupBadge` is opt-in
  // (default off) so this stays scoped to the Certificates LIST — CertificateCell
  // is also reused by OwnerPage/LeadsTab, which don't pass it and render
  // unchanged.
  const groupRole: CertificateGroupRole | undefined = !showGroupBadge
    ? undefined
    : certificate.parent_certificate_id
    ? "child"
    : certificate.group_divisibility
    ? "parent"
    : undefined;
  return (
    <Link
      component={RouterLink}
      to={ROUTES.CERTIFICATE(certificate.id)}
      state={referrer}
      height="100%"
      sx={{
        textDecoration: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        // VRG-231 review round 3 item 1 — fill the cell rather than shrink-to-fit.
        // The text stack below was pinned at a fixed `width` (260px on the
        // Certificates list) inside a 434px cell, so a group PARENT row — which
        // spends ~56px of that stack on the role glyph + "N items" badge, both
        // flexShrink:0 — clipped its title ("Omega Speedmaster Moonw…") while
        // ~106px of the cell sat unused. Measured, not estimated: title
        // scrollWidth 204 vs clientWidth 194 on the parent row, untruncated on
        // the identical title one row down with no badge. §49 permits clipping a
        // CELL value, but not while the column has room to spare.
        width: "100%",
        minWidth: 0,
      }}
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
    >
      <Stack
        direction="row"
        spacing={3}
        alignItems="center"
        display="flex"
        sx={{ p: 2, pl: 0, width: "100%", minWidth: 0 }}
        justifyContent="flex-start"
      >
        <Image
          src={getProductThumbnail(certificate.product?.images) ?? ""}
          alt={certificate.product?.title}
          fit="cover"
          sx={(theme) => ({
            width: size,
            height: size,
            border: `1px solid ${theme.palette.grey[200]}`,
            flexShrink: 0,
          })}
        />
        {/* `width` is now a FLEX BASIS, not a cap — the stack still starts at the
            caller's width but grows into whatever the cell has spare (VRG-231
            review round 3 item 1). minWidth:0 keeps `noWrap` ellipsis working
            when the cell is genuinely narrower than the content. */}
        <Stack spacing={2} sx={{ minWidth: 0, flex: `1 1 ${typeof width === "number" ? `${width}px` : width}` }}>
          {/* VRG-491 — the integrity signals moved UP here, off the metadata line below.
              A subline carries attributes of the row's IDENTITY (what this certificate is
              and what it is called); an exception is not an attribute of the identity, and
              sitting among them it read as one more property rather than as something
              wrong. It now sits adjacent to the thing it qualifies.

              `minWidth: 0` on the title so it, and only it, absorbs the truncation — the
              indicators must never be the thing that gets clipped, since a count clipped
              to nothing is indistinguishable from no invalid scans at all. The group-role
              glyph + member-count badge (VRG-231) share this row for the same reason. */}
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
            {/* The native `title` attribute, matching the page header's own truncated
                title. The ellipsis is now reachable in practice — before the tree-data
                span was allowed to shrink (see `.MuiDataGrid-treeDataGroupingCell > span`
                in Table.tsx) this text never truncated; it overflowed the cell and was
                sliced off by `overflow: hidden`, taking the indicators with it. Now that
                the title is the thing that yields, the full name has to stay recoverable
                without opening the row. */}
            <Typography
              variant="body3"
              sx={{ fontWeight: 500, minWidth: 0 }}
              noWrap
              title={certificate.product?.title}
            >
              {certificate.product?.title}
            </Typography>
            {groupRole && (
              <CertificateGroupRoleIcon
                role={groupRole}
                status={certificate.status}
                divisibility={certificate.group_divisibility}
                size={16}
              />
            )}
            {groupRole === "parent" && <GroupMemberCountBadge certificateId={certificate.id} />}
            {/* An EVENT COUNT, never a badge: "3 invalid scans" as a badge would assert
                that the certificate is invalid, which is false. Severity, colour and the
                fill/outline channel are all decided in `utils/invalidScanSeverity`. */}
            <InvalidScanIndicator
              count={certificate.invalid_scan_count}
              reasonCounts={certificate.invalid_reason_counts}
            />
            {/* A STATE, so it is a badge. Renders on `true` only — `false` and NULL (a tag
                with no tamper loop, which is most of them) both render nothing rather than
                claiming a seal we never checked is intact. */}
            <TamperBadge tampered={certificate.is_tag_tampered} size="dense" />
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body4" color="text.secondary" noWrap>
              {certificate.id}
            </Typography>
            {/* VRG-361 — stays on the metadata line, unlike the two above. This one IS an
                attribute of the row's identity (whose data this is), which is exactly the
                distinction that moved the other two. */}
            <ExampleKindBadge kind={certificate.example_kind} size="dense" />
          </Stack>
        </Stack>
      </Stack>
    </Link>
  );
};

/**
 * One size + radius for EVERY entity thumbnail in a table row — product, package, company.
 *
 * They were measurably different: the package/product image rendered 46x46 at 8px while the
 * company avatar rendered 48x48 at 6px, so a row with both had two thumbnails that were almost
 * the same and visibly not. Two numbers agreeing by coincidence is what drifts; a shared
 * constant is what holds.
 */
export const CELL_THUMB_SIZE = 46
export const CELL_THUMB_RADIUS = '8px'

export const CompanyCell = (props: { company: any }) => {
  const { company } = props;
  const theme = useTheme();
  const referrer = useReferrerState();

  return (
    <Link
      component={RouterLink}
      to={ROUTES.INTERNAL_COMPANY(company.subdomain ?? company.id)}
      state={referrer}
      underline="none"
      height="100%"
      sx={{
        textDecoration: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
      }}
    >
      <Stack
        direction="row"
        spacing={3}
        alignItems="center"
        justifyContent="flex-start"
      >
        <CompanyAvatar
          logo={company.logo}
          name={company.name}
          size={CELL_THUMB_SIZE}
          radius={CELL_THUMB_RADIUS}
        />
        <Typography variant="body3" color="text.primary" sx={{ minWidth: 0, fontWeight: 500 }} noWrap>
          {company.name}
        </Typography>
      </Stack>
    </Link>
  );
};

/**
 * A subscription reference cell: the plan a company is on, and when they started
 * on it, linking to where that subscription can actually be managed.
 *
 * ## Where it links, and why that is not `/internal/subscriptions`
 *
 * There is NO per-subscription detail route — `ROUTES` has `INTERNAL_SUBSCRIPTIONS`
 * (the cross-company list) and nothing below it, and `SubscriptionsPage` holds its
 * search in local state, so a link to the list cannot even be pre-filtered to the
 * row you clicked. Landing someone on an unfiltered list of every subscription in
 * the product is a worse answer than the cell they started from.
 *
 * The company detail page's Billing tab (`?tab=plan`) IS the single-subscription
 * surface: plan, status, period, and the comp/cancel actions. So that is the
 * destination. `?tab=plan` rather than `?tab=billing` is deliberate upstream — see
 * `CompanyPage`'s note on why the tab's LABEL moved to "Billing" while its VALUE
 * stayed `plan` so existing deep links keep working.
 *
 * ## Two lines, matching the entity-preview shape used by every other reference cell
 *
 * Primary = the plan name (what they bought), secondary = the start date (when).
 * `started_at` is the subscription row's own creation, NOT `current_period_start`,
 * which rolls every billing period and would render "when this month began" under a
 * heading that says when they bought.
 *
 * §36: this is the FIRST such preview, hand-rolled once, and it lives here beside
 * `CompanyCell`/`PaymentCell` rather than in the page that needed it — so the
 * SubscriptionsPage's plain-text plan column has something to converge onto rather
 * than becoming the second copy.
 */
export const SubscriptionCell = (props: {
  subscription: {
    plan_title?: string | null;
    plan_interval?: string | null;
    started_at?: string | Date | null;
  } | null;
  /** Subdomain preferred over id — matches CompanyCell's own route argument. */
  companyRef?: string | null;
}) => {
  const { subscription, companyRef } = props;
  const referrer = useReferrerState();

  if (!subscription) {
    return (
      <Stack height="100%" justifyContent="center">
        <Typography variant="body3" color="text.disabled">
          {EMPTY_VALUE}
        </Typography>
      </Stack>
    );
  }

  const started = subscription.started_at
    ? date(
        typeof subscription.started_at === "string"
          ? subscription.started_at
          : subscription.started_at.toISOString(),
      )
    : null;

  const body = (
    <Stack justifyContent="center" sx={{ minWidth: 0, height: "100%" }}>
      <Typography
        className="SubscriptionCell-text"
        variant="body3"
        color="text.primary"
        sx={{ fontWeight: 500 }}
        noWrap
      >
        {subscription.plan_title || "Unnamed plan"}
      </Typography>
      {started && (
        <Typography variant="caption" color="text.secondary" noWrap>
          Started {started}
        </Typography>
      )}
    </Stack>
  );

  // No company to hang the link on (shouldn't happen — a subscription belongs to a
  // company — but a cell must not throw on a shape it did not expect).
  if (!companyRef) {
    return body;
  }

  return (
    <Link
      component={RouterLink}
      to={`${ROUTES.INTERNAL_COMPANY(companyRef)}?tab=plan`}
      state={referrer}
      underline="none"
      height="100%"
      sx={{
        textDecoration: "none",
        display: "flex",
        alignItems: "center",
        minWidth: 0,
        "&:hover .SubscriptionCell-text": { textDecoration: "underline" },
      }}
    >
      {body}
    </Link>
  );
};

export const CompanyGroupCell = (props: { companies: any[]; route?: any }) => {
  const { companies = [], route } = props;
  // Before the early return below — hooks can't run conditionally.
  const referrer = useReferrerState();

  // A single company renders the richer single CompanyCell (logo + name
  // inline, linked to the internal company page) instead of a one-avatar
  // group.
  if (companies.length === 1) {
    return <CompanyCell company={companies[0]} />;
  }

  return (
    <AvatarGroup
      max={7}
      sx={{ p: 2, pl: 0, justifyContent: "center", width: "100%" }}
    >
      {companies.map((company, index) => {
        // Companies render as square blocks (CompanyAvatar) — distinct from the
        // round user avatars — matching CompanyCell + the assign-companies modal.
        const avatar = (
          <CompanyAvatar logo={company.logo} name={company.name} size={48} />
        );

        if (route) {
          return (
            <Tooltip
              key={company.id ?? `${company.name}-${index}`}
              title={company.name}
            >
              <Link
                component={RouterLink}
                to={route(company.id)}
                state={referrer}
                underline="none"
                height="100%"
                sx={{ marginLeft: index != 0 ? -2 : 0, display: "flex" }}
              >
                {avatar}
              </Link>
            </Tooltip>
          );
        }

        return (
          <Tooltip
            key={company.id ?? `${company.name}-${index}`}
            title={company.name}
          >
            {avatar}
          </Tooltip>
        );
      })}
    </AvatarGroup>
  );
};

// Initials for avatar placeholders — first letter of the first two words,
// uppercased. Matches the sidebar profile (SidebarUserBlock).
const getUserInitials = (value?: string | null) => {
  if (!value) return "";
  return value
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
};

// Mode-aware initials-avatar styling, identical to the sidebar profile chip:
// neutral surface + secondary text + subtle ring. Reads cleanly in dark mode
// (light-grey letters on dark-zinc) instead of the washed-out silhouette.
const initialsAvatarSx = (size: number) => (theme: any) => ({
  width: size,
  height: size,
  fontSize: Math.round(size / 3),
  fontWeight: 600,
  // VERA-914: primary[50] resolved to zinc-900 (#18181b, near-black) in dark mode —
  // a harsh ring that read as a black border on selected rows. primary[200] (zinc-700,
  // #3f3f46) is one step lighter than the zinc-800 fill: a subtle "dark zinc" ring.
  border: `1px solid ${theme.palette.primary[200]}`,
  backgroundColor: theme.palette.primary[100],
  color: theme.palette.text.secondary,
});

/**
 * Shared presentational user block: avatar + name + email (matches Figma).
 * Used by UserCell (wrapped in a link for tables) and anywhere a user needs to
 * be shown as a row (e.g. bulk-action modals). No navigation of its own.
 */
export const UserBlock = (props: { user: any; size?: number; secondary?: React.ReactNode }) => {
  const { user, size = 40, secondary: secondaryOverride } = props;
  // VERA-304: one resolver, shared with OwnerPreview and the owner detail header.
  // `hasRealName` was computed here as `Boolean(user?.name || …)` and was correct —
  // but the server used to send `name: 'Unknown'` for a nameless owner, which is
  // truthy, so this branch believed every such row HAD a name. Fixed at the source
  // (`customer-data.aggregate.ts`); `getPersonLabel` also trims, so a whitespace-only
  // name can't resurrect the same bug.
  const { primary: resolvedPrimary, secondary: resolvedSecondary, hasName: hasRealName } =
    getPersonLabel(user);
  const name = resolvedPrimary;
  const primary = resolvedPrimary || 'Anonymous';
  // Photo → initials from a real NAME → first letter of the EMAIL → the generic
  // person glyph, and only then.
  //
  // The glyph used to cover the email-only case too, on the reasoning that it should
  // read as "a person we don't know" rather than assert an "S". In practice nobody
  // decodes it that way: a column of identical grey silhouettes beside three
  // different addresses reads as broken, and it is unscannable next to a real "JD".
  // The distinction it was protecting is already carried, and carried better, by the
  // text — a known user shows a NAME with the email beneath, an email-only row shows
  // only the address.
  //
  // The glyph is kept for the one case that has no letter to show: a genuinely
  // anonymous row (a scan with no user and no email), where an empty Avatar child is
  // what renders it.
  const initials = hasRealName
    ? getUserInitials(name)
    : user?.email
      ? user.email.trim()[0]?.toUpperCase() ?? ''
      : '';
  // Callers can override the second line (e.g. a scan row shows location, not email).
  const secondary = secondaryOverride !== undefined ? secondaryOverride : (resolvedSecondary ?? undefined);
  // A larger avatar (>=44, e.g. the sparse Scans list which isn't a dense table) scales
  // the text up a step too, so the block stays proportionate. Tables keep the compact 40.
  const large = size >= 44;
  return (
    <Stack
      direction="row"
      spacing={3}
      alignItems="center"
      justifyContent="flex-start"
      sx={{ minWidth: 0 }}
    >
      <Avatar src={user?.avatar || undefined} alt={name ?? undefined} sx={initialsAvatarSx(size)}>
        {initials}
      </Avatar>
      <Stack sx={{ minWidth: 0 }}>
        <Typography variant={large ? 'body2' : 'body3'} sx={{ fontWeight: 500 }} noWrap>
          {primary}
        </Typography>
        {/* VRG-361 — on the SECONDARY line, for the same reason as ProductCell: the
            name is what truncates. Wired here in UserBlock rather than in UserCell so
            every surface that renders a person as a row (the Owners list, the assign
            modals, a scan row) badges a seeded owner identically, without each one
            remembering to. `example_kind` is absent on most user objects, and the
            badge renders null for those. */}
        {(secondary || user?.example_kind) && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            {secondary && (
              <Typography variant={large ? 'body3' : 'body4'} color="text.secondary" noWrap>
                {secondary}
              </Typography>
            )}
            <ExampleKindBadge kind={user?.example_kind} size="dense" />
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

export const UserCell = (props: { user: any; route?: any }) => {
  const { user, route } = props;
  // Before the early returns below — hooks can't run conditionally.
  const referrer = useReferrerState();
  // No owner/user → render an empty cell (no avatar or initials fallback). Scoped
  // to table cells; UserBlock usages that pass an explicit name (e.g. the Scans
  // list's "Anonymous" rows) are unaffected.
  const hasUser = Boolean(
    user?.name || user?.first_name || user?.last_name || user?.email,
  );
  if (!hasUser) {
    return <Stack sx={{ py: 2 }} justifyContent="center" height="100%" />;
  }
  if (!route) {
    return (
      <Stack sx={{ py: 2 }} justifyContent="center" height="100%">
        <UserBlock user={user} />
      </Stack>
    );
  }
  return (
    <Link
      component={RouterLink}
      to={route}
      state={referrer}
      underline="none"
      height="100%"
      sx={{
        textDecoration: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        py: 2,
      }}
    >
      <UserBlock user={user} />
    </Link>
  );
};

/**
 * Shared presentational company block — the company analogue of UserBlock:
 * the canonical rounded-square CompanyAvatar (logo → initials → glyph) + name.
 * Use this (not a hand-rolled avatar+label) anywhere a company shows as a row —
 * e.g. an invoice "Placed by" cell for a subscription charge (§36).
 */
export const CompanyBlock = (props: {
  company: any;
  size?: number;
  secondary?: React.ReactNode;
}) => {
  const { company, size = 40, secondary } = props;
  const name = company?.name ?? "";
  return (
    <Stack
      direction="row"
      spacing={3}
      alignItems="center"
      justifyContent="flex-start"
      sx={{ minWidth: 0 }}
    >
      <CompanyAvatar logo={company?.logo} name={name} size={size} />
      <Stack sx={{ minWidth: 0 }}>
        <Typography variant="body3" sx={{ fontWeight: 500 }} noWrap>
          {name || EMPTY_VALUE}
        </Typography>
        {secondary && (
          <Typography variant="body4" color="text.secondary" noWrap>
            {secondary}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
};

/**
 * Copy for a certificate that has no owner because the brand still holds it. Kept beside
 * the component so the Owner column, its CSV export and the scan-filter message can all
 * read one string instead of three that drift.
 */
export const BRAND_HELD_QUALIFIER = "Not yet sold";

/**
 * The OTHER no-owner state — activated, but no owner row — renders the shared
 * `EMPTY_VALUE` dash. It used to read "Unclaimed", which was dropped because
 * unclaimed is exactly equivalent to ownerless: the word is derivable from the
 * emptiness and adds only ink. The dash still carries the thing the previous
 * BLANK could not — that we looked and there is nothing — so the "missing data"
 * impression the blank produced does not come back with it.
 */

/**
 * A certificate's Owner cell — the single place that decides how an ABSENT owner reads.
 *
 * Three states, and the distinction between the last two is the whole point (§36: this
 * exists as one component precisely so the states cannot drift apart across surfaces):
 *
 * 1. **Has an owner** → the person treatment: `UserCell` (circular avatar, name, email),
 *    linked to their owner page.
 * 2. **No owner, not activated** → the brand still holds it. Name the COMPANY, using
 *    `CompanyBlock` — the rounded-SQUARE `CompanyAvatar`, never the circular person
 *    avatar — with a muted "Not yet sold" subline. The square shape plus the qualifier
 *    are what stop a brand named "Louis Vuitton" reading as a consumer of that name.
 *    Unlinked: a company is not an owner record, so there is no owner page to reach.
 * 3. **No owner, ACTIVATED** → genuinely anomalous: it was sold and the owner is missing
 *    or unregistered. Naming the company here would assert that the brand owns something
 *    already in a consumer's hands, so it does NOT. A muted `EMPTY_VALUE` dash instead of
 *    the previous blank — neutral about who holds it, but not mistakable for missing data,
 *    which is the complaint the blank produced in the first place.
 *
 * Display-only. Ownership data is untouched; the brand as a first-class origin link in
 * provenance is VRG-319.
 */
export const CertificateOwnerCell = (props: {
  /** The certificate row: supplies the owner, the status and (optionally) the company. */
  certificate: any;
  /**
   * Fallback company for state 2, for lists whose rows do not embed one (a company-scoped
   * list already knows whose it is). Ignored when the row carries `company`.
   */
  company?: any;
}) => {
  const { certificate, company } = props;
  const owner = certificate?.customer;
  const hasOwner = Boolean(
    certificate?.owner_id || certificate?.customer_id || owner,
  );

  if (hasOwner) {
    return (
      <UserCell
        user={owner}
        route={
          certificate?.owner_id
            ? ROUTES.OWNER(certificate.owner_id)
            : undefined
        }
      />
    );
  }

  const brandHeld = isBrandHeldCertificate(certificate);
  const brand = certificate?.company ?? company;

  // Brand-held but we cannot name the brand — fall back to the neutral label rather than
  // rendering an avatar with no name, which would read worse than the blank it replaced.
  if (brandHeld && brand?.name) {
    return (
      <Stack sx={{ py: 2 }} justifyContent="center" height="100%">
        <CompanyBlock company={brand} secondary={BRAND_HELD_QUALIFIER} />
      </Stack>
    );
  }

  return (
    <Stack sx={{ py: 2 }} justifyContent="center" height="100%">
      <Typography variant="body3" color="text.secondary">
        {EMPTY_VALUE}
      </Typography>
    </Stack>
  );
};

export const UserGroupCell = (props: {
  users: any[];
  route: any;
  /** Avatar diameter. Default 48; pass 40 to match the single UserCell avatar. */
  size?: number;
  /** Alignment within the cell. Default 'center'. */
  align?: "left" | "center" | "right";
  /** Max circles shown (incl. the "+N" surplus). Default 7. */
  max?: number;
}) => {
  const { users = [], route, size = 48, align = "center", max = 7 } = props;
  const referrer = useReferrerState();

  // Popover state for the "+N" surplus roster (used only in the multi-user branch
  // below). It MUST be declared above the single-user early return: `users.length`
  // changes as a row's data loads or is edited, so hooks below the return would run
  // on some renders and not others. That is the hooks-order crash.
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [search, setSearch] = React.useState("");

  // A single user renders the richer single UserCell (avatar + name + email
  // inline) instead of a one-avatar group.
  if (users.length === 1) {
    return <UserCell user={users[0]} route={route(users[0]?.id)} />;
  }

  // MUI AvatarGroup is `flex-direction: row-reverse` (first avatar stacks on top,
  // the "+N" surplus sits at the right) — which FLIPS justify-content, so
  // visual-left = flex-end and visual-right = flex-start. (This also fixes the
  // left avatar getting clipped: when left-aligned it hugs the cell's left edge.)
  const justifyContent =
    align === "left" ? "flex-end" : align === "right" ? "flex-start" : "center";

  // Clicking the "+N" surplus opens a searchable roster popover (reusing the same
  // Search + UserBlock components as the Assign-users modal's user list), sized to
  // show ~5 rows then scroll. Click-triggered (not hover) so the search input is
  // a stable, interactive target. (`anchorEl` / `search` are declared at the top of
  // the component — see the hooks-order note there.)
  const open = Boolean(anchorEl);
  const closePopover = () => {
    setAnchorEl(null);
    setSearch("");
  };
  const q = search.trim().toLowerCase();
  const filteredUsers = q
    ? users.filter((u) => {
        const name = (getNameOrEmailSlug(u) || "").toLowerCase();
        const email = (u?.email || "").toLowerCase();
        return name.includes(q) || email.includes(q);
      })
    : users;

  return (
   <>
    <AvatarGroup
      max={max}
      // Style the "+N" surplus avatar to match the rest (dark fill, light text)
      // instead of MUI's default light pill.
      componentsProps={{ additionalAvatar: { sx: initialsAvatarSx(size) } }}
      // `renderSurplus` returns the CONTENT that goes INSIDE MUI's surplus avatar,
      // so we wrap a full-size <span> (the click target covering the whole circle),
      // not an Avatar (which would nest avatar-in-avatar). Clicking it opens the
      // roster popover below.
      renderSurplus={(surplus) => (
        <span
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setAnchorEl(e.currentTarget);
          }}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
            cursor: "pointer",
          }}
        >
          +{surplus}
        </span>
      )}
      sx={{ p: 2, pl: 0, justifyContent, width: "100%" }}
    >
      {users.map((user, index) => {
        const name = getNameOrEmailSlug(user);
        const email = user?.email;
        const hasName = Boolean(user?.name || user?.first_name || user?.last_name);
        // Tooltip: "Name\nemail" when a real name exists, else just the email.
        const tip = hasName && email ? `${name}\n${email}` : name || email || "";
        return (
          <Link
            component={RouterLink}
            to={route(user?.id)}
            state={referrer}
            key={user?.id}
            underline="none"
            height="100%"
            sx={{ marginLeft: index != 0 ? -2 : 0, display: "flex" }}
          >
            <Tooltip title={<span style={{ whiteSpace: "pre-line" }}>{tip}</span>} arrow>
              <Avatar
                src={user.avatar || undefined}
                alt={name ?? undefined}
                sx={initialsAvatarSx(size)}
              >
                {getUserInitials(name)}
              </Avatar>
            </Tooltip>
          </Link>
        );
      })}
    </AvatarGroup>

    {/* Roster popover — searchable list of all users, reusing the Search +
        UserBlock components from the Assign-users modal. Sized to fit ~5 rows
        then scroll. Each row links to the user and highlights on hover. */}
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={closePopover}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      // House menu chrome (MENU_PAPER_SX): the 8px trigger gap, the 4px paper inset + the viewport-safe max
      // height, so this roster reads as the same object as every dropdown in the app.
      // `overflow: 'hidden'` stays LAST and deliberately: MENU_PAPER_SX's `overflowY:
      // auto` would hand scrolling to the Paper, but the scrolling element here is the
      // inner Box (it owns `maxHeight: 260`) and the search must stay pinned above it.
      slotProps={{
        paper: { sx: { ...MENU_PAPER_SX, width: 320, overflow: "hidden" } },
      }}
    >
      {/* 4px, matching the list inset MENU_PAPER_SX gives a real MuiList — there is no
          MuiList here (the rows are Boxes), so its `& .MuiMenu-list` rule finds nothing
          and the inset is stated directly. Search field and row highlights then share
          one left edge at 4 (paper) + 4 = 8px. */}
      <Stack sx={{ px: "4px", pt: "4px", pb: "4px" }}>
        <Search
          value={search}
          onChange={setSearch}
          onClear={() => setSearch("")}
          placeholder="Search users"
          fullWidth
          debounce={false}
        />
      </Stack>
      <Box
        sx={{
          // ~5 rows (each ≈ 56px) then scroll.
          maxHeight: 260,
          overflowY: "auto",
          // Same 4px inset as the search Stack above — the row's own padding comes from
          // MENU_ITEM_SX, so nothing is added here on top of it.
          px: "4px",
          py: 0,
        }}
      >
        {filteredUsers.length === 0 ? (
          <Typography variant="body3" color="text.secondary" sx={{ px: 2, py: 3 }}>
            No users match your search.
          </Typography>
        ) : (
          filteredUsers.map((user) => (
            <Box
              key={user?.id}
              component={RouterLink}
              to={route(user?.id)}
              state={referrer}
              onClick={closePopover}
              sx={(theme) => ({
                // House per-row convention: 12px on all four sides, the nested-radius
                // highlight, and the 4px gap between consecutive rows. Was 8/6 with a
                // 4px radius, which read tighter than the menus beside it.
                ...MENU_ITEM_SX,
                display: "block",
                textDecoration: "none",
                color: "inherit",
                "&:hover": { backgroundColor: theme.palette.action.hover },
              })}
            >
              <UserBlock user={user} size={32} />
            </Box>
          ))
        )}
      </Box>
    </Popover>
   </>
  );
};

import { StatusChip } from "components";

export const StatusChipCell = (props: { status: any; statuses: object }) => {
  const { status, statuses } = props;
  return (
    <StatusChip
      size="small"
      statuses={statuses}
      status={status}
    />
  );
};

import { CATEGORY_ICONS } from "constants/icons";
// VRG-628 — PRODUCT TYPE glyphs live in their own map; see the `kind` prop below.
import { getProductTypeIcon } from "constants/productTypeIcon";
import {
  Icon,
  RepeatIcon,
  ShoppingBagIcon,
  FishSymbolIcon,
  SprayCanIcon,
  Disc3Icon,
  AmphoraIcon,
  BookCopy,
  ShirtIcon,
  WineIcon,
  WatchIcon,
  GemIcon,
  UsersRoundIcon,
  BoxIcon,
  CarIcon,
  TicketIcon,
  // VERA-246 — form×capability product-matrix category icons (see gen-matrix.mjs ICON map).
  WalletIcon,
  LuggageIcon,
  PaletteIcon,
  CameraIcon,
  WalletCardsIcon,
  BanknoteIcon,
  StampIcon,
  BookOpenIcon,
  TrophyIcon,
  BlocksIcon,
  GuitarIcon,
  UtensilsCrossedIcon,
  CigaretteIcon,
  BikeIcon,
  SailboatIcon,
  SmartphoneIcon,
  HeadphonesIcon,
  ArmchairIcon,
  LampIcon,
  Grid2x2Icon,
  DumbbellIcon,
  TentIcon,
  CannabisIcon,
  PillBottleIcon,
  StethoscopeIcon,
  TabletsIcon,
  CogIcon,
  PlaneIcon,
  CircuitBoardIcon,
  FileTextIcon,
  // VRG-611 — `legal_documents`. Verified present in the pinned lucide-react@0.468.0
  // (dist/esm/icons/scale.js, exported as both `Scale` and `Scale as ScaleIcon`).
  ScaleIcon,
  PenToolIcon,
  FlameIcon,
  // VRG-152 — new/renamed category icons. NOTE: the design spec calls for
  // `CircleStarIcon` (Collectibles) but that glyph isn't in the pinned
  // lucide-react@0.468.0 — substituting the plain `StarIcon` until the pin is
  // bumped (flagged as a follow-up, not silently dropped).
  StarIcon,
  BrushIcon,
  CoinsIcon,
  BiohazardIcon,
} from "lucide-react";
import {
  gemRing,
  bagHand,
  highHeel,
  dress,
  wineGlassBottle,
  glassesSun,
  // VERA-246 — form×capability product-matrix category icons (see gen-matrix.mjs ICON map).
  sneaker,
  belt,
  scarf,
  hatBaseball,
  coinsStack,
  bottlePerfume,
  soapBar,
  candlestickLit,
  cupSaucer,
  tire,
  motorRacingHelmet,
  deskLamp,
  golfDriver,
  targetArrow,
} from "@lucide/lab";

export const CategoryCell = (props: {
  category: any;
  filter: boolean;
  align?: "start" | "center";
  /** When false, render the type label with no surrounding border/pill (parity
   *  with the Product Type cell, VERA-455). Defaults to true for back-compat. */
  bordered?: boolean;
  /**
   * WHICH TAXONOMY the string belongs to (VRG-628). Defaults to `'category'`,
   * so every existing call site is untouched.
   *
   * ⚠️ This is not decoration. The `icons` map below is keyed by product
   * CATEGORY slugs (23) AND product TYPE names (68) at once, and **14 keys
   * serve both** — footwear, handbags, eyewear, jewelry, watches, gemstones,
   * art, trading_cards, collectibles, books, caviar, cannabis,
   * pharmaceuticals, supplements. A rename on one axis therefore repaints the
   * other, silently; the VRG-152 notes further down record that happening to
   * `art` and `collectibles` and call it a cross-feature side effect.
   *
   * Passing `kind="productType"` resolves through `getProductTypeIcon`, which
   * owns all 68 types explicitly, so a type's glyph no longer depends on
   * whether its name happens to collide with a category slug.
   */
  kind?: "category" | "productType";
}) => {
  const {
    category,
    filter = false,
    align = "start",
    bordered = true,
    kind = "category",
  } = props;
  const theme = useTheme();

  // Guard against non-string values
  if (typeof category !== "string") {
    console.warn("CategoryCell received non-string category:", category);
    return null;
  }

  const icons: Record<string, JSX.Element> = {
    // Match the internal sidebar: Subscriptions = RepeatIcon, Orders = ShoppingBagIcon.
    subscription: <RepeatIcon strokeWidth={1} />,
    order: <ShoppingBagIcon strokeWidth={1} />,
    watches: <WatchIcon strokeWidth={1} />,
    gemstones: <GemIcon strokeWidth={1} />,
    jewelry: <Icon iconNode={gemRing} strokeWidth={1} />,
    apparel: <ShirtIcon strokeWidth={1} />,
    // VRG-152: tag-shop's "Fine Art" renamed to "Art" — this key is shared with the
    // (unrelated) general-Products "Art" category type, so both now render Palette
    // (was Amphora for the general-Products category). Flagged as a cross-feature
    // side effect, not a silent regression.
    art: <PaletteIcon strokeWidth={1} />,
    perfume: <SprayCanIcon strokeWidth={1} />,
    music: <Disc3Icon strokeWidth={1} />,
    wine: <WineIcon strokeWidth={1} />,
    "wine_&_spirits": <Icon iconNode={wineGlassBottle} strokeWidth={1} />,
    wine_spirits: <Icon iconNode={wineGlassBottle} strokeWidth={1} />,
    handbags: <Icon iconNode={bagHand} strokeWidth={1} />,
    footwear: <Icon iconNode={highHeel} strokeWidth={1} />,
    eyewear: <Icon iconNode={glassesSun} strokeWidth={1} />,
    books: <BookCopy strokeWidth={1} />,
    dresses: <Icon iconNode={dress} strokeWidth={1} />,
    caviar: <FishSymbolIcon strokeWidth={1} />,
    memberships: <UsersRoundIcon strokeWidth={1} />,
    membership_cards: <UsersRoundIcon strokeWidth={1} />,
    clothes: <Icon iconNode={dress} strokeWidth={1} />,
    glasses: <Icon iconNode={glassesSun} strokeWidth={1} />,
    bags: <Icon iconNode={bagHand} strokeWidth={1} />,
    // VRG-152: tag-shop's "Collectibles — Other" renamed to "Collectibles" — this key
    // is shared with the (unrelated) general-Products "Collectibles" category type, so
    // both now render Star (was HeartHandshake for the general-Products category; the
    // spec's `CircleStarIcon` isn't in the pinned lucide-react — see import comment).
    // Flagged as a cross-feature side effect, not a silent regression.
    collectibles: <StarIcon strokeWidth={1} />,
    automotive: <CarIcon strokeWidth={1} />,
    event_tickets: <TicketIcon strokeWidth={1} />,
    other: <BoxIcon strokeWidth={1} />,

    // VERA-246 — full consolidated category set from the tag-shop product matrix
    // (supply/tag-shop-product-matrix.md), one icon per category, per gen-matrix.mjs's
    // ICON map. Keys are the same normalization already applied below.
    aerospace_defense: <PlaneIcon strokeWidth={1} />,
    apparel_clothing: <ShirtIcon strokeWidth={1} />,
    audio_equipment: <HeadphonesIcon strokeWidth={1} />,
    auto_parts: <Icon iconNode={tire} strokeWidth={1} />,
    automobiles: <CarIcon strokeWidth={1} />,
    belts: <Icon iconNode={belt} strokeWidth={1} />,
    bicycles: <BikeIcon strokeWidth={1} />,
    candles: <Icon iconNode={candlestickLit} strokeWidth={1} />,
    cannabis: <CannabisIcon strokeWidth={1} />,
    cigars_tobacco: <CigaretteIcon strokeWidth={1} />,
    coffee_tea: <Icon iconNode={cupSaucer} strokeWidth={1} />,
    // VRG-152 — Coins & Currency renamed to Currency; Coins split out as its own
    // (new) category, below.
    currency: <BanknoteIcon strokeWidth={1} />,
    coins: <CoinsIcon strokeWidth={1} />,
    // VRG-152 — Collectibles — Other renamed to Collectibles (icon set above, shared
    // with the general-Products "collectibles" key).
    comics: <BookOpenIcon strokeWidth={1} />,
    electronics: <SmartphoneIcon strokeWidth={1} />,
    // VRG-152 — Cosmetics & Makeup renamed to Cosmetics, icon changed to Brush.
    cosmetics: <BrushIcon strokeWidth={1} />,
    documents: <FileTextIcon strokeWidth={1} />,
    // VRG-610's PRODUCT_CATEGORIES slug is `legal_documents`, not `documents`, so the
    // row above never matched it and the category fell through to the generic box.
    // A balance scale, matching `categoryIcon.tsx` — the two maps must agree on this
    // slug. Deliberately NOT the same glyph as `documents:` above: that row is the
    // broader Documents PRODUCT TYPE and FileText describes the medium, while the
    // CATEGORY is legal instruments and Scale names the subject. Also not StampIcon —
    // that belongs to the philatelic `stamps` category below, and two unrelated
    // categories drawing one glyph in the same table is the collision to avoid.
    legal_documents: <ScaleIcon strokeWidth={1} />,
    // Same glyph as Memberships on purpose: a credential IS a membership-shaped
    // artifact (a card a person carries), so one concept, one icon.
    credentials: <UsersRoundIcon strokeWidth={1} />,
    electrical_components: <CircuitBoardIcon strokeWidth={1} />,
    // VRG-152 — Fine Art renamed to Art (icon set above, shared with the
    // general-Products "art" key); Fine Jewelry renamed to Jewelry (already covered
    // by the shared "jewelry" key above — same gemRing icon).
    firearms: <Icon iconNode={targetArrow} strokeWidth={1} />,
    // VRG-152 — Hazardous (new).
    hazardous: <BiohazardIcon strokeWidth={1} />,
    fragrance_perfume: <Icon iconNode={bottlePerfume} strokeWidth={1} />,
    furniture: <ArmchairIcon strokeWidth={1} />,
    golf_equipment: <Icon iconNode={golfDriver} strokeWidth={1} />,
    gourmet_food: <UtensilsCrossedIcon strokeWidth={1} />,
    hats: <Icon iconNode={hatBaseball} strokeWidth={1} />,
    home_décor: <LampIcon strokeWidth={1} />,
    industrial_machinery: <CogIcon strokeWidth={1} />,
    lighters: <FlameIcon strokeWidth={1} />,
    lighting: <Icon iconNode={deskLamp} strokeWidth={1} />,
    luggage: <LuggageIcon strokeWidth={1} />,
    marine_boats: <SailboatIcon strokeWidth={1} />,
    medical_devices: <StethoscopeIcon strokeWidth={1} />,
    motorcycles: <Icon iconNode={motorRacingHelmet} strokeWidth={1} />,
    musical_instruments: <GuitarIcon strokeWidth={1} />,
    // VRG-152 — Outdoor & Camping renamed to Camping.
    camping: <TentIcon strokeWidth={1} />,
    pharmaceuticals: <PillBottleIcon strokeWidth={1} />,
    photography: <CameraIcon strokeWidth={1} />,
    // VRG-152 — Precious Metals renamed to Bullion.
    bullion: <Icon iconNode={coinsStack} strokeWidth={1} />,
    // VRG-152 — Rare Books renamed to Books (already covered by the shared "books"
    // key above — same BookCopy icon).
    rugs_carpets: <Grid2x2Icon strokeWidth={1} />,
    scarves: <Icon iconNode={scarf} strokeWidth={1} />,
    sculpture: <AmphoraIcon strokeWidth={1} />,
    skincare: <Icon iconNode={soapBar} strokeWidth={1} />,
    sneakers: <Icon iconNode={sneaker} strokeWidth={1} />,
    sporting_goods: <DumbbellIcon strokeWidth={1} />,

    // ── Canonical product-type ALIASES (VRG-141 product-types.ts long forms) ──
    // The keys above are the SHORT historical forms ("eyewear", "handbags"). The
    // canonical best_for_product_types vocabulary uses longer names ("Eyewear & Sunglasses"),
    // and the normalizer below turns "&"+spaces into a single "_", so those never
    // matched and silently fell through to the generic box. Alias them onto the
    // existing icon rather than duplicating the glyph, so there is still one icon
    // per concept. Added 2026-08-06 after measuring 12 of 21 shop categories
    // falling back.
    eyewear_sunglasses: <Icon iconNode={glassesSun} strokeWidth={1} />,
    handbags_purses: <Icon iconNode={bagHand} strokeWidth={1} />,
    wallets_small_leather_goods: <Icon iconNode={bagHand} strokeWidth={1} />,
    luggage_travel_bags: <Icon iconNode={bagHand} strokeWidth={1} />,
    scarves_silk: <Icon iconNode={dress} strokeWidth={1} />,
    hats_headwear: <ShirtIcon strokeWidth={1} />,
    sporting_goods_equipment: <DumbbellIcon strokeWidth={1} />,
    industrial_parts_machinery: <CogIcon strokeWidth={1} />,
    auto_parts_components: <Icon iconNode={tire} strokeWidth={1} />,
    loose_gemstones_diamonds: <GemIcon strokeWidth={1} />,
    sculpture_design_objects: <PaletteIcon strokeWidth={1} />,
    toys_action_figures: <BoxIcon strokeWidth={1} />,
    supplements_nutraceuticals: <BoxIcon strokeWidth={1} />,
    lighters_accessories: <BoxIcon strokeWidth={1} />,
    // Wine and Spirits are SEPARATE canonical product types (the shared
    // "wine_spirits" key above covers the combined legacy label).
    spirits: <Icon iconNode={wineGlassBottle} strokeWidth={1} />,
    // VRG-152 — Sports Memorabilia renamed to Memorabilia.
    memorabilia: <TrophyIcon strokeWidth={1} />,
    stamps: <StampIcon strokeWidth={1} />,
    supplements: <TabletsIcon strokeWidth={1} />,
    ticketing_access: <TicketIcon strokeWidth={1} />,
    toys: <BlocksIcon strokeWidth={1} />,
    trading_cards: <WalletCardsIcon strokeWidth={1} />,
    vinyl_records: <Disc3Icon strokeWidth={1} />,
    wallets: <WalletIcon strokeWidth={1} />,
    writing_instruments: <PenToolIcon strokeWidth={1} />,
  };

  return (
    <Stack
      justifyContent="center"
      alignItems={align}
      sx={{ minHeight: "100%", width: "100%" }}
    >
      <Stack
        spacing={2}
        direction="row"
        alignItems="center"
        sx={{
          borderRadius: 2,
          border:
            filter || !bordered
              ? "none"
              : `1px solid ${theme.palette.primary[300]}`,
          px: !bordered ? 0 : filter ? 1 : 3,
          py: !bordered ? 0 : filter ? 0 : 1,
          pr: !bordered ? 0 : filter ? 1 : 4,
          maxWidth: "fit-content",
        }}
      >
        {/* Pin the glyph box AND the svg. lucide renders at its own default and the
            two consumers of CategoryCell (the tag-shop card and the "+N more" popover)
            sit in containers with different type scales, so the icons read at different
            sizes. Fixing it here keeps them identical wherever the cell is used. */}
        <Stack
          sx={{
            minWidth: 20,
            '& svg': { width: 20, height: 20, flexShrink: 0 },
          }}
        >
          {/* VRG-628 — a PRODUCT TYPE resolves through its own map, which holds
              all 68 explicitly, rather than through the shared `icons` object
              above where 14 keys double as category slugs. Categories keep the
              existing path untouched; `kind` defaults to `'category'`. */}
          {kind === "productType" ? (
            getProductTypeIcon(category)
          ) : (
            icons[
              category
                .trim()
                .toLowerCase()
                .replace(/[\s&]+/g, "_")
            ] ?? <BoxIcon strokeWidth={1} />
          )}
        </Stack>
        <Typography variant="body3" sx={{ maxWidth: "fit-content" }} noWrap>
          {capitalize(category.replace(/_/g, " "))}
        </Typography>
      </Stack>
    </Stack>
  );
};

import { PAYMENT_METHOD_STATUSES } from "constants/statuses";

export const PaymentCell = (props: {
  paymentMethod: any;
  showDefault?: boolean;
  sx?: object;
  link?: boolean;
}) => {
  const { paymentMethod, showDefault = false, sx, link = true } = props;
  const referrer = useReferrerState();
  const isDefault = paymentMethod?.default ?? paymentMethod?.isDefault ?? false;
  // Normalize the varied caller shapes (brand|type, last4|details.card_number)
  // to what the shared PaymentMethodPreview reads.
  const normalized = {
    ...paymentMethod,
    brand: paymentMethod?.brand || paymentMethod?.type,
    last4:
      paymentMethod?.last4 ||
      paymentMethod?.details?.card_number?.slice(-4) ||
      "",
  };
  return (
    <Link
      component={link ? RouterLink : "div"}
      to={ROUTES.SETTINGS_COMPANY_PAYMENT_METHODS}
      state={referrer}
      underline="none"
      sx={{
        textDecoration: "none",
        display: "flex",
        alignItems: "stretch",
        width: "100%",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={3}
        sx={{ minHeight: "100%", p: 2, pl: 0, pr: 0, width: "100%", ...sx }}
      >
        <PaymentMethodPreview method={normalized} />
        {isDefault && showDefault && (
          <Stack sx={{ flexShrink: 0 }}>
            <StatusChip status="default" statuses={PAYMENT_METHOD_STATUSES} />
          </Stack>
        )}
      </Stack>
    </Link>
  );
};

import { ADDRESS_STATUSES } from "constants/statuses";
import { AddressPreview } from "components";

/**
 * §36: was a hand-rolled second address preview (image + label + subtitle)
 * with its own type→icon ternary duplicating `getAddressIcon` and covering
 * `AddressType` values ('event', 'warehouse', 'shipping') that no longer
 * exist in the type. Converged onto the shared `AddressPreview` — this cell
 * now only adapts the two accepted shapes (address-book vs. a raw Shopify
 * order address) into `AddressLike` and adds the table-specific link + the
 * optional "default" badge.
 */
export const AddressCell = (props: {
  address: any;
  showDefault?: boolean;
  sx?: object;
  link?: boolean;
}) => {
  const { address, showDefault = false, sx, link = true } = props;
  const referrer = useReferrerState();

  return (
    <Link
      component={link ? RouterLink : "div"}
      to={ROUTES.SETTINGS_COMPANY_ADDRESSBOOK}
      state={referrer}
      underline="none"
      height="100%"
      sx={{
        textDecoration: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Stack
        direction="row"
        spacing={4}
        alignItems="center"
        sx={{ minHeight: "100%", p: 2, pl: 0, width: '100%', ...sx }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <AddressPreview
            address={{
              type: address.type,
              icon: address.icon,
              label: address.label,
              // Accepts both the canonical address-book shape (`address`/
              // `postal_code`) and the raw Shopify order shape (`address1`/
              // `zip`) so either can be passed straight through.
              address: address.address ?? address.address1,
              city: address.city,
              state: address.state,
              postal_code: address.postal_code ?? address.zip,
              country: address.country,
            }}
          />
        </Box>
        {address.default && showDefault && (
          <StatusChip status="default" statuses={ADDRESS_STATUSES} />
        )}
      </Stack>
    </Link>
  );
};

import { Progress } from "components";

/**
 * Inventory bar colour, one step BRIGHTER in light mode.
 *
 * `INVENTORY_STATUSES[status].bar` is `green[700]` (#157f3c), which reads dark and muted on a
 * light track next to the much brighter `green[400]` (#4ade80) used for the status LABEL — the
 * bar and its own label looked like different greens. Light now uses `green[600]` (#16a249).
 *
 * Not brighter than 600, and the reason is measured rather than aesthetic: a filled bar is a
 * non-text UI component, so WCAG 1.4.11 wants >=3:1 against the adjacent surface. Against the
 * light track (grey[100]) green600 is 3.03:1 — the brightest shade that still clears it. green500
 * is 2.10:1 and green400, the label colour asked for, is 1.59:1, which would leave the bar barely
 * distinguishable from the track it sits in.
 *
 * Applied HERE rather than in `constants/statuses.ts` where the token belongs, because that file
 * is another agent's in-flight work and is off-limits. Move it there once that lands: this helper
 * is a workaround for a file lock, not the right long-term home.
 *
 * Reads the green off `theme.palette`, and keys on the status rather than comparing colour values.
 * The first version compared `INVENTORY_STATUSES[status].bar === palette.green[700]` against the
 * bare `palette` module — which has a TYPE declaration in scope here but no runtime import, so
 * `tsc --noEmit` passed clean while every inventory row threw
 * `ReferenceError: palette is not defined` and the whole table hit the error boundary. A
 * typecheck is not a smoke test.
 *
 * TOTAL as of 2026-08-22. It used to index `INVENTORY_STATUSES[status]` directly, so an
 * unrecognised status — which the caller could produce, since it seeded `status` as `''` and
 * relied on three `if`s covering every number — threw `Cannot read properties of undefined
 * (reading 'bar')` and took the whole table down through the error boundary again. Same class
 * of fault as the `palette` bug above, one line further along. The lookup now goes through
 * `inventoryStatusMeta`, which cannot return undefined.
 */
const inventoryBarColor = (status: string, theme: any): string =>
  theme.palette.mode === "light" && status === "plenty"
    ? theme.palette.green[600]
    : inventoryStatusMeta(status).bar;
import {
  inventoryStatusFromCapacity,
  inventoryStatusMeta,
} from "utils/inventory";

export const InventoryLevelCell = (props: {
  inventory: any;
  sx?: object;
  /** Wrap the bar in a Tooltip showing Tags/Products in stock + Gap (default true). */
  showTooltip?: boolean;
  // A `compact` prop used to exist here to drop the on-bar bottom labels and their offsets,
  // for callers embedding the bar beside already-centred content. Every caller now gets that
  // treatment, because the labels are gone for everyone (see the block above), so the prop
  // selected between two identical renders. It is removed rather than left as a no-op: a
  // prop that does nothing still reads as a choice, and the next caller would set it.
}) => {
  const { inventory, sx, showTooltip = true } = props;
  const theme = useTheme();

  // ── The gauge has exactly FOUR inputs (VERA-455): Ordered, Available, Threshold,
  //    Uncertified products. Incoming is deliberately NOT one of them.
  //
  //    A tag in transit cannot be applied to a product today, so counting it toward the
  //    level would overstate readiness — the same error the deprecated `ordered_qty` made
  //    when it told a brand it had capacity for 74 certificates while it physically held
  //    none. The bar answers "can I ship certified product now", and only Available can.
  //
  //    Ordered is here for the opposite reason: it is the CONTEXT that makes a low
  //    Available legible. Available 0 against Ordered 100 means "your stock is coming";
  //    Available 0 against Ordered 0 means "you need to buy tags". Same fill, opposite
  //    action — and without Ordered on the bar the operator cannot tell them apart. It is
  //    a muted annotation, never part of the fill or the colour, for the same reason
  //    Incoming is excluded from those.

  // Demand = uncertified assigned products' in-stock quantity (they still need a
  // tag). Falls back to summing all assigned products when the field is absent.
  const forProductsQty =
    inventory.uncertified_qty != null
      ? inventory.uncertified_qty
      : inventory.for_products.reduce(
          (sum, obj) => sum + (obj["quantity"] || 0),
          0
        );
  const availableQty = inventory.available_qty ?? 0;
  const atVeraQty = inventory.at_vera_qty ?? 0;
  // What the bar PLOTS: capacity = Available − demand. Server-computed; recomputed here
  // only so a caller that hasn't been migrated still renders the same number rather than
  // a silent NaN.
  const gapValue = inventory.capacity ?? availableQty - forProductsQty;
  // The threshold is the gauge's scale as well as its tick, so it has to be a usable number
  // before anything is plotted against it. The server defaults an unset threshold to 0
  // (`inv.threshold ?? 0`), and `?? 0` does not catch NaN, so both cases arrive here.
  const threshold = Number.isFinite(Number(inventory.threshold))
    ? Number(inventory.threshold)
    : 0;
  // Shared derivation (working-conventions §36) — the status column and the detail page read
  // the same helper, so the chip beside this bar can never disagree with it. The three
  // fall-through `if`s that lived here seeded `status` as `''`, which is the value that threw
  // one line later whenever capacity was not a finite number.
  const status = inventoryStatusFromCapacity(gapValue, threshold);

  // ── ONE axis across all three segments ────────────────────────────────────────────────
  //
  // The track is a single continuous scale — `−2·scale` at the far left, 0 at the centre,
  // `+2·scale` at the far right — drawn as three segments so the threshold can be a hard
  // boundary rather than an overlaid marker: left = [−2s, 0] (rotated, so it grows leftward
  // from the centre), middle = [0, s], end = [s, 2s]. The centre is where a shortfall becomes
  // a surplus, and the middle/end seam is the threshold tick.
  //
  // For the scale to be honest, equal VALUE spans must occupy equal PIXEL spans: the left
  // segment covers 2s and each right segment covers s, so the flex weights below are 2/1/1.
  // They were previously `minWidth: 50%` on each half with `flex-basis: auto` inside the
  // right half, which made a segment's width depend on how wide its bottom LABEL happened to
  // be — the scale drifted with the text sitting under it.
  //
  // A threshold of 0 means "no alert level configured" — the server defaults it that way
  // (`inv.threshold ?? 0`) — not "alert at zero", and it cannot BE the scale: a zero-wide
  // span has no positions on it, which is what fed 0/0 → NaN into the bar. Fall back to the
  // row's own magnitudes so the gauge still reads; the tick label is suppressed in that case
  // (below), because a tick at 0 names nothing. When a threshold IS set, `scale === threshold`
  // and nothing about the geometry changes.
  const scaleCandidate = Math.max(availableQty, forProductsQty, 1);
  const scale =
    threshold > 0
      ? threshold
      : Number.isFinite(scaleCandidate) && scaleCandidate > 0
        ? scaleCandidate
        : 1;

  // All three are TOTAL — every real number maps to a value inside the segment's own range.
  // The chains they replace left gaps (`gapValue === 0` fell through every branch of
  // `startBarValue` and returned `undefined`) and `endBarValue` returned `threshold - 1`
  // against a segment whose `min` IS the threshold, i.e. a deliberate −1 unit → −4% → a bar
  // translated fully off its own track.
  const startBarValue = () => (gapValue < 0 ? Math.min(-gapValue, scale * 2) : 0);
  const middleBarValue = () => Math.min(Math.max(gapValue, 0), scale);
  const endBarValue = () => Math.min(Math.max(gapValue, scale), scale * 2);

  // ── Why this cell carries NO text labels (VRG-397) ────────────────────────────────────
  //
  // The gauge used to print three figures BENEATH the track: the signed capacity at the far
  // left, a muted "N ordered" beside it, and the threshold at the middle/end seam. USER
  // DIRECTIVE (2026-08-24), stated twice: "fix levels - should be in one line." They are gone
  // rather than moved inline, because every one of them restated a column already on the same
  // ROW of the same table, and a value cannot be un-duplicated by relocating it:
  //
  //   beneath-bar "-26"        == the Capacity column   (same `gapValue`, same status tint)
  //   beneath-bar "N ordered"  == the Ordered column    (`at_vera_qty`)
  //   beneath-bar "25"         == the Threshold column
  //
  // Measured on the running table before the change (1280x800): the level cell rendered "-26"
  // at x=373 while the Capacity column rendered "-26" at x=654 — the same number, twice, 281px
  // apart on one row. That is not information; it is the same fact competing with itself. On a
  // SURPLUS row it was worse: the cell read "12 ordered | 25 | +70" against an Ordered column
  // of 12, a Threshold column of 25 and a Capacity column of 70. Three labels, three duplicates.
  //
  // The threshold is the one with a real claim to a place ON the bar, since its POSITION is
  // meaning the column cannot carry. But the position is drawn either way: the seam between
  // the middle and end segments IS the threshold, and it stays visible as the gap in the
  // track. Only the numeral moved out, and it moved to a column two along that prints it in
  // full, plus the tooltip below.
  //
  // So the deletion is not a loss of the four figures — Ordered, Available, Uncertified,
  // Capacity and Threshold all remain one hover away on this bar's own tooltip, and four of
  // the five are columns on this row. What it buys is a gauge that is a gauge: one line, one
  // continuous track, read by shape and colour, with the arithmetic living where arithmetic
  // belongs.
  //
  // It also fixes a defect the removed comments claimed was already fixed. Those comments said
  // the 18px `mt` had to be UNCONDITIONAL because a per-segment offset once split the track
  // into pieces at different heights. The offset was duly made unconditional — and the track
  // still split, because the offset was never the cause. The cause is that each segment is its
  // own `Progress`, each renders its own label row, and only SOME segments carry a label; under
  // the parent's `alignItems: 'center'` a labelled segment is taller than an unlabelled one, so
  // their tracks centre at different `y`. Measured before this change: a 9px step on an 8px
  // track, in EVERY row and every data range, with the direction flipping by which segment
  // happened to hold the label. With no labels anywhere the three segments are identical by
  // construction, so the step is 0 and cannot come back on some other row's data.
  //
  // Do NOT reintroduce a label here without first removing the column it duplicates — and note
  // that a label on SOME segments is what reintroduces the split, so any future label belongs
  // outside the three `Progress` elements, not in one of them. That was the standing trade the
  // previous wording argument ("-26" vs "Short 26") was really about, and it resolves the other
  // way once you notice the column was always there.

  const bar = (
    // ── Why flex weights and not percentages ──────────────────────────────────────────────
    //
    // The halves were `minWidth: '50%'` each with a 2px margin between them, so the track
    // demanded 302px inside a 300px column — and `minWidth` is a floor, so flexbox could not
    // shrink it back. Every row overflowed by 2px, which is what pushed the threshold label
    // hard against the right-hand edge, where it read as the axis maximum rather than as a
    // tick three-quarters along. (2px is small; it was NOT the whole of the visible breakage.
    // The louder half was arithmetic — see `endBarValue` and `Progress.normalise`.)
    //
    // `flex: 2/1/1` with a zero BASIS gives the three segments 2:1:1 of the available width
    // AFTER the gaps are subtracted — so the track always fits, and equal value spans occupy
    // equal pixel spans: the left segment covers 2·scale in two flex units, each right segment
    // covers scale in one. `minWidth: 0` is required on each, because a flex item's default
    // `min-width: auto` refuses to shrink below its content, and these items carry nowrap
    // labels — the other half of why the row overflowed.
    //
    // The three are SIBLINGS in one row rather than a left half plus a nested right pair. The
    // nesting cost exact equality: the inner gap was subtracted only from the right half's
    // share, so its two segments came out ~1px/unit tighter than the left's. One row, one
    // uniform gap, one rate.
    //
    // The weights are applied by position from the parent, because `Progress` hardcodes
    // `flexGrow: 1` on its own root with a default `auto` basis and routes its `sx` prop to
    // the inner LinearProgress — so neither the basis nor the weight is reachable from the
    // child side.
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.5}
      sx={{
        minHeight: "100%",
        width: "100%",
        "& > *": { minWidth: 0 },
        "& > *:nth-of-type(1)": { flex: "2 1 0" },
        "& > *:nth-of-type(2)": { flex: "1 1 0" },
        "& > *:nth-of-type(3)": { flex: "1 1 0" },
        ...sx,
      }}
    >
        <Progress
          value={startBarValue()}
          color={inventoryBarColor(status, theme)}
          showCircles={false}
          min={0}
          max={2 * scale}
          rounded="right"
          sx={{
            transform: "rotate(180deg)",
          }}
          // Explicit, not omitted. `Progress` treats a MISSING `bottomLabels` as "label the
          // ends with min/max", so dropping the prop does not remove the labels — it swaps
          // them for the segment's own bounds, and this gauge's segments are internal
          // arithmetic (0/50, 0/25, 25/50), which is worse than what was there before.
          bottomLabels={[null, null]}
        />
        <Progress
          value={middleBarValue()}
          color={inventoryBarColor(status, theme)}
          showCircles={false}
          min={0}
          max={scale}
          rounded={
            middleBarValue() > 0 && middleBarValue() < scale ? "right" : "none"
          }
          bottomLabels={[null, null]}
        />
        <Progress
          value={endBarValue()}
          color={inventoryBarColor(status, theme)}
          showCircles={false}
          min={scale}
          max={scale * 2}
          rounded="right"
          bottomLabels={[null, null]}
        />
    </Stack>
  );

  if (!showTooltip) return bar;

  return (
    <Tooltip
      arrow
      placement="top"
      // Sit the tooltip right on top of the bar rather than floating above it.
      slotProps={{
        popper: {
          modifiers: [{ name: "offset", options: { offset: [0, -8] } }],
        },
      }}
      title={
        <Stack spacing={0.5} sx={{ py: 0.5, minWidth: 140 }}>
          {/* The gauge's four inputs, then what they produce. No Incoming row: it feeds
              nothing here, and listing it would imply otherwise. "Ordered" is the count of
              tags still with Vera, NOT the old lifetime `ordered_qty` — these four
              reconcile, which is the whole reason the counter is gone (VERA-455). */}
          <Stack direction="row" justifyContent="space-between" spacing={3}>
            <span>Ordered</span>
            <strong>{atVeraQty}</strong>
          </Stack>
          <Stack direction="row" justifyContent="space-between" spacing={3}>
            <span>Available</span>
            <strong>{availableQty}</strong>
          </Stack>
          <Stack direction="row" justifyContent="space-between" spacing={3}>
            <span>Uncertified products</span>
            <strong>{forProductsQty}</strong>
          </Stack>
          <Stack direction="row" justifyContent="space-between" spacing={3}>
            <span>Capacity</span>
            <strong>
              {gapValue > 0 ? `+${gapValue}` : gapValue}
            </strong>
          </Stack>
          <Stack direction="row" justifyContent="space-between" spacing={3}>
            <span>Threshold</span>
            <strong>
              {inventory.threshold}
              {inventory.threshold_type === "percentage" ? "%" : ""}
            </strong>
          </Stack>
        </Stack>
      }
    >
      {bar}
    </Tooltip>
  );
};
